import { useCallback, Dispatch } from 'react'
import req from '@sportninja/common/api/request'
import { NotificationItem, PaginationMeta } from '../components/types'
import {
  NotificationsAction,
  NotificationFilter,
} from './useNotificationsReducer'

/**
 * API response structure for notifications endpoint
 */
interface NotificationsApiResponse {
  data: NotificationItem[]
  meta?: {
    pagination?: PaginationMeta
  }
}

/**
 * API response structure for unread count endpoint
 */
interface UnreadCountResponse {
  notifications: {
    unread: number
    invitations_unread: number
  }
}

/**
 * Custom hook for managing notifications API operations
 * @param {Dispatch<NotificationsAction>} dispatch - Reducer dispatch function
 * @param {number} currentPage - Current page number
 * @param {NotificationFilter} currentFilter - Current active filter
 * @returns {Object} API operation functions
 */
export const useNotificationsApi = (
  dispatch: Dispatch<NotificationsAction>,
  currentPage: number,
  currentFilter: NotificationFilter
) => {
  console.log('🔄 useNotificationsApi: Initializing API hook')

  /**
   * Fetches notifications from the API
   * @param {number} page - Page number to fetch
   * @param {NotificationFilter | null} selectedFilter - Optional filter override
   */
  const fetchNotifications = useCallback(
    async (page: number, selectedFilter: NotificationFilter | null = null) => {
      try {
        console.log(
          `🔄 fetchNotifications: Fetching page ${page} with filter ${
            selectedFilter || currentFilter
          }`
        )
        dispatch({ type: 'SET_LOADING', payload: true })
        dispatch({ type: 'SET_ERROR', payload: null })

        const filterParam = selectedFilter || currentFilter
        const response = (await req(
          `/notifications?page=${page}&filter=${filterParam}`
        )) as NotificationsApiResponse

        if (response.data && response.data.length > 0) {
          dispatch({ type: 'SET_NOTIFICATIONS', payload: response.data })
          dispatch({
            type: 'SET_PAGINATION',
            payload: response.meta?.pagination || null,
          })
        } else {
          dispatch({ type: 'SET_NOTIFICATIONS', payload: [] })
          dispatch({ type: 'SET_PAGINATION', payload: null })
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Error loading notifications'
        console.error('🚨 fetchNotifications: Error fetching notifications', {
          error,
        })
        dispatch({ type: 'SET_ERROR', payload: errorMessage })
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    },
    [dispatch, currentFilter]
  )

  /**
   * Fetches additional notifications for pagination
   * @param {number} page - Page number to fetch
   * @param {boolean} refresh - If true, replaces current notifications instead of appending
   */
  const fetchMoreNotifications = useCallback(
    async (page: number, refresh: boolean = false) => {
      try {
        console.log(
          `🔄 fetchMoreNotifications: Fetching page ${page}, refresh: ${refresh}`
        )
        dispatch({ type: 'SET_ERROR', payload: null })

        const response = (await req(
          `/notifications?page=${page}&filter=${currentFilter}`
        )) as NotificationsApiResponse

        if (response.data && response.data.length > 0) {
          if (refresh) {
            dispatch({ type: 'SET_NOTIFICATIONS', payload: response.data })
          } else {
            dispatch({ type: 'APPEND_NOTIFICATIONS', payload: response.data })
          }
          dispatch({
            type: 'SET_PAGINATION',
            payload: response.meta?.pagination || null,
          })
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Error loading notifications'
        console.error(
          '🚨 fetchMoreNotifications: Error fetching more notifications',
          { error }
        )
        dispatch({ type: 'SET_ERROR', payload: errorMessage })
      }
    },
    [dispatch, currentFilter]
  )

  /**
   * Deletes a notification by ID
   * @param {string} notificationId - ID of notification to delete
   */
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        console.log(
          `🔄 deleteNotification: Deleting notification ${notificationId}`
        )
        await req(`/notifications/${notificationId}`, {
          method: 'DELETE',
        })
        // Refresh the current page after deletion
        await fetchNotifications(currentPage)
      } catch (error) {
        console.error('🚨 deleteNotification: Error deleting notification', {
          error,
        })
        throw error
      }
    },
    [fetchNotifications, currentPage]
  )

  /**
   * Handles filter change and refreshes notifications
   * @param {NotificationFilter} newFilter - New filter value
   */
  const handleFilterChange = useCallback(
    async (newFilter: NotificationFilter) => {
      console.log('🔄 handleFilterChange: Changing filter to:', newFilter)
      dispatch({ type: 'SET_FILTER', payload: newFilter })
      dispatch({ type: 'SET_PAGE', payload: 1 })
      dispatch({ type: 'SET_NOTIFICATIONS', payload: [] })
      await fetchNotifications(1, newFilter)
    },
    [dispatch, fetchNotifications]
  )

  /**
   * Fetches the unread notification count from API
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      console.log('🔄 fetchUnreadCount: Fetching unread notification counts')
      const response = (await req(
        '/notifications/count_unread'
      )) as UnreadCountResponse
      if (response && response.notifications) {
        dispatch({
          type: 'SET_UNREAD_COUNTS',
          payload: {
            unread: response.notifications.unread || 0,
            invitations_unread: response.notifications.invitations_unread || 0,
          },
        })
      }
    } catch (error) {
      console.error('🚨 fetchUnreadCount: Error fetching unread counts', {
        error,
      })
    }
  }, [dispatch])

  return {
    fetchNotifications,
    fetchMoreNotifications,
    deleteNotification,
    handleFilterChange,
    fetchUnreadCount,
  }
}
